import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { CyberpunkTableCell, CyberpunkTableRow, getDiscountColor } from './shared/StyledComponents';
import { cyberpunkColors } from '../theme/cyberpunkTheme';

// Table configurations for each type
const TABLE_CONFIGS = {
  ri: {
    title: 'Reserved Instance Offerings',
    columns: [
      { key: 'type', label: 'Offering Type', width: 200 },
      { key: 'year', label: 'Year' },
      { key: 'payment option', label: 'Payment Option' },
      { key: 'rate', label: 'Rate/Hr', format: (v) => `$${v}` },
      { key: 'discount', label: 'Discount', isDiscount: true },
    ],
  },
  sp: {
    title: 'Savings Plan Offerings',
    columns: [
      { key: 'type', label: 'Offering Type', width: 200 },
      { key: 'year', label: 'Year' },
      { key: 'payment option', label: 'Payment Option' },
      { key: 'rate', label: 'Rate/Hr', format: (v) => `$${v}` },
      { key: 'discount', label: 'Discount', isDiscount: true },
    ],
  },
  market: {
    title: 'AWS RI Marketplace Offers',
    columns: [
      { key: 'count', label: 'Items Available', width: 200 },
      { key: 'duration', label: 'Duration (months)' },
      { key: 'upfront price', label: 'Upfront Price', format: (v) => `$${v}` },
      { key: 'rate', label: 'Rate/Hr', format: (v) => `$${v}` },
      { key: 'discount', label: 'Discount', isDiscount: true },
    ],
  },
};

/**
 * Unified pricing table component
 * @param {string} type - Table type: 'ri', 'sp', or 'market'
 * @param {Array} data - Array of pricing data
 * @param {number} odRate - On-demand rate for discount calculation
 */
const PricingTable = ({ type, data, odRate }) => {
  const config = TABLE_CONFIGS[type];

  if (!config) {
    console.error(`Unknown table type: ${type}`);
    return null;
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  // Sort by rate (lowest first)
  const sorted = [...data].sort((a, b) => parseFloat(a.rate) - parseFloat(b.rate));

  // Calculate discount percentage
  const calculateDiscount = (rate) => {
    if (!odRate || odRate === 0) return 0;
    return Math.round(100 - (parseFloat(rate) / parseFloat(odRate)) * 100);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontFamily: '"Orbitron", sans-serif',
          color: cyberpunkColors.neonCyan,
          textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
          mb: 2,
          fontSize: '1rem',
          letterSpacing: '0.05em',
        }}
      >
        {config.title}
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          background: 'transparent',
          border: '1px solid rgba(0, 255, 255, 0.2)',
          borderRadius: 1,
          boxShadow: '0 0 15px rgba(0, 255, 255, 0.1)',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {config.columns.map((col) => (
                <CyberpunkTableCell key={col.key}>{col.label}</CyberpunkTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, index) => {
              const discount = calculateDiscount(row.rate);
              return (
                <CyberpunkTableRow key={index}>
                  {config.columns.map((col) => {
                    let value;
                    if (col.isDiscount) {
                      value = (
                        <span style={{ color: getDiscountColor(discount), fontWeight: 600 }}>
                          {discount}%
                        </span>
                      );
                    } else if (col.format) {
                      value = col.format(row[col.key]);
                    } else {
                      value = row[col.key];
                    }

                    return (
                      <CyberpunkTableCell
                        key={col.key}
                        style={col.width ? { width: col.width } : {}}
                      >
                        {value}
                      </CyberpunkTableCell>
                    );
                  })}
                </CyberpunkTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PricingTable;
